// Enhanced Notification Settings Component
import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { notificationsAPI, trainingGroupsAPI } from '../../services/api';
import { usePushNotifications } from '../../hooks/usePushNotifications.js';
import { useNotifications } from '../notifications/NotificationProvider';

/**
 * Component for managing both notification preferences and push notification settings
 */
export default function NotificationSettings() {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotifications();
  const [isSaving, setIsSaving] = useState(false);

  const {
    isSupported,
    permission,
    isSubscribed,
    isLoading: pushLoading,
    error: pushError,
    requestPermission,
    subscribe,
    unsubscribe,
    testNotification,
    canRequestPermission,
    canSubscribe,
    canUnsubscribe,
    getDebugInfo,
    getGlobalState,
    resetGlobalState,
  } = usePushNotifications();

  // Fetch notification preferences
  const {
    data: preferences,
    isLoading: preferencesLoading,
    error: preferencesError,
  } = useQuery('notificationPreferences', notificationsAPI.getPreferences, {
    retry: 2,
    onError: (error) => {
      showError('Failed to load notification preferences');
    },
  });

  // Fetch user's training groups for availability reminder info
  const { data: userTrainingGroups, isLoading: trainingGroupsLoading } = useQuery(
    'userTrainingGroups',
    trainingGroupsAPI.getGroups,
    {
      retry: 2,
      onError: (error) => {
        console.error('Failed to load training groups:', error);
      },
    }
  );

  // Update preferences mutation
  const updatePreferencesMutation = useMutation(
    (data) => notificationsAPI.updatePreferences(data),
    {
      onMutate: async (newData) => {
        // Cancel any outgoing refetches
        await queryClient.cancelQueries('notificationPreferences');

        // Snapshot the previous value
        const previousPreferences = queryClient.getQueryData('notificationPreferences');

        // Optimistically update to the new value
        queryClient.setQueryData('notificationPreferences', (old) => ({
          ...old,
          ...newData,
        }));

        // Return a context object with the snapshotted value
        return { previousPreferences };
      },
      onError: (err, newData, context) => {
        // If the mutation fails, use the context to roll back
        queryClient.setQueryData('notificationPreferences', context.previousPreferences);
        showError('Failed to save preferences. Please try again.');
      },
      onSuccess: (data) => {
        // Update the cache with the returned data from server
        queryClient.setQueryData('notificationPreferences', data);
        showSuccess('Preferences saved successfully!');
      },
      onSettled: () => {
        setIsSaving(false);
        // Always refetch after error or success
        queryClient.invalidateQueries('notificationPreferences');
      },
    }
  );

  // Handle preference changes
  const handlePreferenceChange = async (field, value) => {
    setIsSaving(true);
    const updatedPreferences = {
      ...preferences,
      [field]: value,
    };

    updatePreferencesMutation.mutate(updatedPreferences);
  };

  // Handle push notification permission request
  const handleRequestPermission = async () => {
    try {
      await requestPermission();
    } catch (error) {
      showError('Failed to request notification permission');
    }
  };

  // Handle push subscription
  const handleSubscribe = async () => {
    try {
      await subscribe();
    } catch (error) {
      showError('Failed to subscribe to push notifications');
    }
  };

  // Handle push unsubscription
  const handleUnsubscribe = async () => {
    try {
      await unsubscribe();
    } catch (error) {
      showError('Failed to unsubscribe from push notifications');
    }
  };

  // Handle test notification
  const handleTestNotification = async () => {
    try {
      await testNotification();
    } catch (error) {
      showError('Failed to send test notification');
    }
  };

  // Helper function to convert day number to day name
  const getDayName = (dayNumber) => {
    const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    return days[dayNumber] || 'Unknown';
  };

  if (preferencesLoading || trainingGroupsLoading) {
    return (
      <div className="rounded-lg bg-white p-6 shadow-md">
        <h2 className="mb-4 text-xl font-semibold">Notification Settings</h2>
        <div className="animate-pulse">
          <div className="mb-3 h-4 w-3/4 rounded bg-gray-200"></div>
          <div className="mb-3 h-4 w-1/2 rounded bg-gray-200"></div>
          <div className="h-4 w-2/3 rounded bg-gray-200"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg bg-white p-6 shadow-md">
      <h2 className="mb-4 text-xl font-semibold">Notification Settings</h2>

      {/* Notification Preferences Section */}
      <div className="mb-6">
        <h3 className="mb-4 text-lg font-medium text-gray-900">Notification Preferences</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-gray-700">Availability Reminders</label>
              <p className="text-xs text-gray-500">
                Get reminded to set your availability for upcoming training sessions
              </p>
            </div>
            <button
              onClick={() =>
                handlePreferenceChange(
                  'availability_reminders',
                  !preferences?.availability_reminders
                )
              }
              disabled={isSaving}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 ${
                preferences?.availability_reminders ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  preferences?.availability_reminders ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-gray-700">Session Scheduled</label>
              <p className="text-xs text-gray-500">
                Notify when you're added to a training session
              </p>
            </div>
            <button
              onClick={() =>
                handlePreferenceChange('session_scheduled', !preferences?.session_scheduled)
              }
              disabled={isSaving}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 ${
                preferences?.session_scheduled ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  preferences?.session_scheduled ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-gray-700">Session Rescheduled</label>
              <p className="text-xs text-gray-500">
                Notify when training session times change or are cancelled
              </p>
            </div>
            <button
              onClick={() =>
                handlePreferenceChange('session_rescheduled', !preferences?.session_rescheduled)
              }
              disabled={isSaving}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 ${
                preferences?.session_rescheduled ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  preferences?.session_rescheduled ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-gray-700">Schedule Reminders</label>
              <p className="text-xs text-gray-500">
                Get reminders about unscheduled training sessions (coaches only)
              </p>
            </div>
            <button
              onClick={() =>
                handlePreferenceChange('schedule_reminders', !preferences?.schedule_reminders)
              }
              disabled={isSaving}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 ${
                preferences?.schedule_reminders ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  preferences?.schedule_reminders ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-gray-700">Admin Announcements</label>
              <p className="text-xs text-gray-500">
                Important announcements from coaches and administrators
              </p>
            </div>
            <div className="flex items-center">
              <span className="mr-2 text-xs text-gray-500">Always enabled</span>
              <div className="relative inline-flex h-6 w-11 flex-shrink-0 cursor-not-allowed rounded-full border-2 border-transparent bg-blue-600">
                <span className="pointer-events-none inline-block h-5 w-5 translate-x-5 transform rounded-full bg-white shadow ring-0" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Push Notifications Section */}
      {isSupported && (
        <div>
          <h3 className="mb-4 text-lg font-medium text-gray-900">Push Notifications</h3>

          {/* Permission Status */}
          <div className="mb-4">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Permission Status:</span>
              <span
                className={`rounded-full px-2 py-1 text-xs font-medium ${
                  permission === 'granted'
                    ? 'bg-green-100 text-green-800'
                    : permission === 'denied'
                      ? 'bg-red-100 text-red-800'
                      : 'bg-yellow-100 text-yellow-800'
                }`}
              >
                {permission === 'granted'
                  ? 'Granted'
                  : permission === 'denied'
                    ? 'Denied'
                    : 'Not Requested'}
              </span>
            </div>

            {/* Subscription Status */}
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Subscription Status:</span>
              <span
                className={`rounded-full px-2 py-1 text-xs font-medium ${
                  isSubscribed ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                }`}
              >
                {isSubscribed ? 'Subscribed' : 'Not Subscribed'}
              </span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-2">
            {canRequestPermission && (
              <button
                onClick={handleRequestPermission}
                disabled={pushLoading}
                className="inline-flex items-center rounded-md border border-transparent bg-blue-600 px-3 py-2 text-sm font-medium leading-4 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
              >
                {pushLoading ? 'Requesting...' : 'Enable Notifications'}
              </button>
            )}

            {canSubscribe && (
              <button
                onClick={handleSubscribe}
                disabled={pushLoading}
                className="inline-flex items-center rounded-md border border-transparent bg-green-600 px-3 py-2 text-sm font-medium leading-4 text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50"
              >
                {pushLoading ? 'Subscribing...' : 'Subscribe to Push Notifications'}
              </button>
            )}

            {canUnsubscribe && (
              <button
                onClick={handleUnsubscribe}
                disabled={pushLoading}
                className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium leading-4 text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
              >
                {pushLoading ? 'Unsubscribing...' : 'Unsubscribe'}
              </button>
            )}

            {isSubscribed && (
              <button
                onClick={handleTestNotification}
                disabled={pushLoading}
                className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium leading-4 text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
              >
                {pushLoading ? 'Sending...' : 'Send Test Notification'}
              </button>
            )}
          </div>

          {/* Error Display */}
          {pushError && (
            <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-red-800">
                    Push notification error: {pushError}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Auto-subscription Info */}
          {permission === 'granted' && !isSubscribed && (
            <div className="mt-4 rounded-md border border-blue-200 bg-blue-50 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-blue-800">
                    Auto-subscription enabled: You'll be automatically subscribed to push
                    notifications when you grant permission.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Debug Information (only show in development) */}
          {process.env.NODE_ENV === 'development' && (
            <div className="mt-4 rounded-md border border-gray-200 bg-gray-50 p-4">
              <h4 className="mb-2 text-sm font-medium text-gray-900">Debug Information</h4>
              <div className="space-y-2 text-xs">
                <div>
                  <strong>Push Manager Debug:</strong>
                  <pre className="mt-1 rounded bg-white p-2 text-xs">
                    {JSON.stringify(getDebugInfo(), null, 2)}
                  </pre>
                </div>
                <div>
                  <strong>Global State:</strong>
                  <pre className="mt-1 rounded bg-white p-2 text-xs">
                    {JSON.stringify(getGlobalState(), null, 2)}
                  </pre>
                </div>
                <button
                  onClick={resetGlobalState}
                  className="rounded bg-red-100 px-2 py-1 text-xs text-red-700 hover:bg-red-200"
                >
                  Reset Global State
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Not Supported Message */}
      {!isSupported && (
        <div className="rounded-md border border-yellow-200 bg-yellow-50 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-yellow-800">
                Push notifications are not supported in your browser. You can still receive email
                notifications.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Availability Reminder Info */}
      {userTrainingGroups && userTrainingGroups.length > 0 && (
        <div className="mt-6 rounded-md border border-gray-200 bg-gray-50 p-4">
          <h4 className="mb-2 text-sm font-medium text-gray-900">Availability Reminders</h4>
          <p className="text-xs text-gray-600">
            You'll receive reminders to set your availability for the following training groups:
          </p>
          <ul className="mt-2 space-y-1">
            {userTrainingGroups.map((group) => (
              <li key={group.id} className="text-xs text-gray-600">
                • {group.name}
                {group.availability_reminder_enabled && (
                  <span className="ml-2 text-green-600">
                    (Reminders enabled - {getDayName(group.availability_reminder_day)}s)
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
